"use client";

/**
 * trust_blocks renderer — mirrors the v2 PrinciplesSection layout: bordered
 * card containing a horizontal row of icon + title + desc with subtle dashed
 * dividers between columns.
 *
 * Data: section.cards (icon, title, description). Section content can carry
 * an optional eyebrow/title above the row but most usage is item-only.
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

export function TrustBlocksSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const heading = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const cards = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(heading || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-8">
            {heading && (
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{heading}</h2>
            )}
            {subtitle && (
              <p className="mt-2 text-current/70 text-base">{subtitle}</p>
            )}
          </div>
        )}
        <div className="relative rounded-xl border border-gray-200 bg-white p-6 lg:p-10 shadow-[0_2px_24px_-12px_rgba(15,77,31,0.12)]">
          <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-green-300 to-transparent" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {cards.map((c, i) => (
              <div
                key={c.id}
                className="group relative flex items-start gap-4 rounded-md p-3 transition-colors hover:bg-green-50/40"
              >
                {i < cards.length - 1 && (
                  <span className="absolute -right-4 top-1/2 hidden h-16 -translate-y-1/2 border-r border-dashed border-gray-200 lg:block" />
                )}
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-full bg-green-200 blur-md opacity-60" />
                  <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-[0_8px_18px_-6px_rgba(30,140,58,0.55)]">
                    <SafeIcon name={c.icon} className="h-6 w-6" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  {c.title && (
                    <h3 className="font-semibold text-sm text-gray-900 mb-1 leading-tight">
                      {c.title}
                    </h3>
                  )}
                  {c.description && (
                    <CardDescriptionHtml
                      as="p"
                      text={c.description}
                      className="text-[12.5px] text-gray-500 leading-snug"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
